import { Loader2 } from "lucide-react";
import type { MailProviderPreset } from "@/lib/types/mail";
import { GoogleBrandIcon } from "./icons/GoogleBrandIcon";
import { MicrosoftBrandIcon } from "./icons/MicrosoftBrandIcon";
import { ProviderCard } from "./ProviderCard";
import { ProviderGrid } from "./ProviderGrid";
import {
  mailConnectFindProvider,
  mailConnectGmailOAuthAvailable,
  mailConnectMicrosoftOAuthAvailable,
  mailConnectOtherProviders,
} from "./providerConnectOrder";
import { MailConnectLegalNotice } from "./MailConnectLegalNotice";
import { cn } from "@/lib/cn";

type Props = {
  providers: MailProviderPreset[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onGoogleConnect: () => void;
  onMicrosoftConnect: () => void;
  googleBusy: boolean;
  microsoftBusy: boolean;
};

export function AccountConnectPicker({
  providers,
  selectedId,
  onSelect,
  onGoogleConnect,
  onMicrosoftConnect,
  googleBusy,
  microsoftBusy,
}: Props) {
  const gmailOAuth = mailConnectGmailOAuthAvailable(providers);
  const microsoftOAuth = mailConnectMicrosoftOAuthAvailable(providers);
  const gmail = mailConnectFindProvider(providers, "gmail");
  const microsoft = mailConnectFindProvider(providers, "microsoft");
  const manual = mailConnectFindProvider(providers, "manual");
  const others = mailConnectOtherProviders(providers);

  const quickCount = [gmailOAuth || gmail, microsoftOAuth || microsoft, manual].filter(Boolean).length;

  return (
    <div className="space-y-4">
      {quickCount > 0 ? (
        <div className="space-y-2">
          <div
            className={cn(
              "grid gap-2",
              quickCount >= 3 ? "grid-cols-3" : quickCount === 2 ? "grid-cols-2" : "grid-cols-1"
            )}
          >
            {gmailOAuth ? (
              <button
                type="button"
                onClick={onGoogleConnect}
                disabled={googleBusy || microsoftBusy}
                className={cn(
                  "group relative flex w-full flex-row items-center gap-2 rounded-xl border px-2.5 py-2.5 text-left transition-all sm:gap-3 sm:px-3",
                  "border-[var(--mail-border-soft)] bg-[var(--mail-surface)] hover:border-[var(--mail-accent)]/30 hover:shadow-[var(--mail-shadow-sm)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mail-accent)] focus-visible:ring-offset-2",
                  "disabled:opacity-50"
                )}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[var(--mail-border-soft)] bg-[var(--mail-surface-elevated)] shadow-sm">
                  {googleBusy ? (
                    <Loader2 className="h-5 w-5 animate-spin text-[var(--mail-muted)]" aria-hidden />
                  ) : (
                    <GoogleBrandIcon className="h-5 w-5" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-semibold text-[var(--mail-text)]">
                    Google
                  </span>
                  <span className="mt-0.5 hidden truncate text-[10px] text-[var(--mail-muted)] sm:block">
                    Connexion rapide
                  </span>
                </span>
              </button>
            ) : gmail ? (
              <ProviderCard
                provider={gmail}
                selected={selectedId === gmail.id}
                onSelect={() => onSelect(gmail.id)}
                layout="row"
              />
            ) : null}

            {microsoftOAuth ? (
              <button
                type="button"
                onClick={onMicrosoftConnect}
                disabled={googleBusy || microsoftBusy}
                className={cn(
                  "group relative flex w-full flex-row items-center gap-2 rounded-xl border px-2.5 py-2.5 text-left transition-all sm:gap-3 sm:px-3",
                  "border-[var(--mail-border-soft)] bg-[var(--mail-surface)] hover:border-[var(--mail-accent)]/30 hover:shadow-[var(--mail-shadow-sm)]",
                  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mail-accent)] focus-visible:ring-offset-2",
                  "disabled:opacity-50"
                )}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border border-[var(--mail-border-soft)] bg-[var(--mail-surface-elevated)] shadow-sm">
                  {microsoftBusy ? (
                    <Loader2 className="h-5 w-5 animate-spin text-[var(--mail-muted)]" aria-hidden />
                  ) : (
                    <MicrosoftBrandIcon className="h-5 w-5" />
                  )}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-xs font-semibold text-[var(--mail-text)]">
                    Microsoft
                  </span>
                  <span className="mt-0.5 hidden truncate text-[10px] text-[var(--mail-muted)] sm:block">
                    Connexion rapide
                  </span>
                </span>
              </button>
            ) : microsoft ? (
              <ProviderCard
                provider={microsoft}
                selected={selectedId === microsoft.id}
                onSelect={() => onSelect(microsoft.id)}
                layout="row"
              />
            ) : null}

            {manual ? (
              <ProviderCard
                provider={{ ...manual, label: "IMAP", hint: manual.hint || "Serveur perso" }}
                selected={selectedId === manual.id}
                onSelect={() => onSelect(manual.id)}
                layout="row"
              />
            ) : null}
          </div>

        </div>
      ) : null}

      {others.length > 0 ? (
        <div className="space-y-2 pt-1">
          <p className="px-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--mail-muted)]">
            Autres fournisseurs
          </p>
          <ProviderGrid
            providers={others}
            selectedId={selectedId}
            onSelect={onSelect}
            columns={7}
          />
        </div>
      ) : null}

      <MailConnectLegalNotice />
    </div>
  );
}
